import { useState, useEffect, useRef } from "react";
import {
  Breadcrumbs,
  AppBar,
  MenuItem,
  Popover,
  Stack,
  Link,
  Toolbar,
  Button,
  Switch,
  FormControlLabel,
  Box,
} from "@mui/material";
import { DensityMedium, NavigateNext, Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { useResponsive } from "../hooks/use-responsive";

import { bgBlur } from "../theme/css";

import { NAV, HEADER } from "./config-layout";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import { authState } from "../recoil/authState";
import { useNavData, useBottomNavData } from "./navData";
import { settingState } from "../recoil/settingState";
import GuideModal from "../component/GuideModal";

const Breadcrumb = (props) => {
  function findBreadcrumbPath(navData, pathSegments) {
    const fullPath = pathSegments.join("/");
    
    // 재귀적으로 path 속성으로 먼저 찾기
    function findByPath(items, parentPath = []) {
      for (const item of items) {
        if (item.path && fullPath.startsWith(item.path)) {
          const result = [...parentPath, item];
          
          // 자식이 있으면 더 구체적인 경로 찾기
          if (item.child && item.child.length > 0) {
            const childResult = findByPath(item.child, result);
            if (childResult) return childResult;
            
            // :id 자식 확인
            const pathParts = fullPath.split("/");
            const itemParts = item.path.split("/");
            if (pathParts.length > itemParts.length) {
              const idChild = item.child.find(c => c.key === ":id");
              if (idChild) return [...result, idChild];
            }
          }
          
          return result;
        }
        
        // 자식이 있으면 재귀
        if (item.child && item.child.length > 0) {
          const childResult = findByPath(item.child, [...parentPath, item]);
          if (childResult) return childResult;
        }
      }
      return null;
    }
    
    // 먼저 path 속성으로 찾기
    const pathResult = findByPath(navData);
    if (pathResult) return pathResult;
    
    // path 속성이 없으면 key 기반으로 찾기 (기존 로직)
    let path = [];
    let currentLevel = navData;
    for (const segment of pathSegments) {
      let match;
      if (currentLevel?.find) {
        match = currentLevel.find((item) => item.key === segment);
      }
      if (match) {
        path.push(match);
        currentLevel = match.child;
      } else {
        break;
      }
    }

    return path;
  }

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean); // Split path and remove empty segments
  const nP = pathSegments.map((p) => {
    if (Number.isNaN(Number(p))) {
      return p;
    }
    return ":id";
  });
  const theme = useTheme();
  // Find the breadcrumb path from navData
  const breadcrumbPath = findBreadcrumbPath(props.navData, nP);
  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      sx={{
        color: theme.palette.common.darkGrey,
        // color: "darkGrey",
        ".MuiBreadcrumbs-separator": {
          margin: "0 4px !important", // Adjust spacing around the separator
        },
      }}
      separator={
        <NavigateNext
          fontSize="20px"
          sx={{ verticalAlign: "middle", color: "#79818A" }}
        />
      }
    >
      {breadcrumbPath
        .filter((item) => item.key !== "modify") // "정보 수정" 제거
        .map((item, index, filteredArray) => {
          const isLast = index === filteredArray.length - 1;
          const originalIndex = breadcrumbPath.indexOf(item);
          const to = "/" + pathSegments.slice(0, originalIndex + 1).join("/");
          return (
            <Link
              sx={{
                fontSize: "16px !important",
                fontWeight: isLast ? "600" : "500", // Semibold : Medium
                color: isLast ? "black !important" : "#79818A",
              }}
              key={item.key}
              // component={RouterLink}
              to={to}
              underline={"none"}
              color={isLast ? "black" : "#79818A"}
            >
              {item.name}
            </Link>
          );
        })}
    </Breadcrumbs>
  );
};

export default function Header() {
  // const nav = useNavigate();
  // console.log('nav :', nav);
  const location = useLocation();
  const topNavData = useNavData(); // 동적으로 navData 가져오기
  const bottomNavData = useBottomNavData();
  const setAuth = useSetRecoilState(authState);
  const auth = useRecoilValue(authState);

  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const theme = useTheme();
  const lgUp = useResponsive("up", "lg");

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };
  const handleClose = () => {
    setOpen(null);
  };
  const logoutHandle = () => {
    setAuth({
      isLoggedIn: false,
      user: null,
      token: null,
    });
    localStorage.clear();
    navigate("login");
  };
  const [setting, setSetting] = useRecoilState(settingState);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(() => {
    // 로컬스토리지에서 툴팁 상태 확인
    try {
      const savedTooltipState = localStorage.getItem("tooltipClosed");
      return savedTooltipState !== "true";
    } catch (error) {
      console.error("로컬스토리지에서 툴팁 상태를 읽는데 실패했습니다:", error);
      return true; // 기본값으로 표시
    }
  });
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    right: "auto",
  });
  const tooltipRef = useRef(null);

  // 페이지 로드 시 가이드가 on이면 모달 열기
  useEffect(() => {
    if (setting.guide) {
      setGuideModalOpen(true);
    }
  }, [setting.guide]);

  // 페이지가 바뀔 때마다 가이드 상태 확인
  useEffect(() => {
    if (setting.guide) {
      setGuideModalOpen(true);
    }
  }, [location.pathname, setting.guide]);

  const handleGuideToggle = () => {
    const newGuideState = !setting.guide;
    setSetting((prev) => {
      return { ...prev, guide: newGuideState };
    });

    // 로컬스토리지에 가이드 설정 저장
    try {
      localStorage.setItem(
        "guideSetting",
        JSON.stringify({ guide: newGuideState })
      );
    } catch (error) {
      console.error(
        "로컬스토리지에 가이드 설정을 저장하는데 실패했습니다:",
        error
      );
    }

    // 가이드가 켜지면 모달 열기
    if (newGuideState) {
      setGuideModalOpen(true);
    }
  };

  const handleGuideModalClose = () => {
    setGuideModalOpen(false);
  };

  const handleTooltipClose = () => {
    setTooltipVisible(false);

    // 로컬스토리지에 툴팁 닫힌 상태 저장
    try {
      localStorage.setItem("tooltipClosed", "true");
    } catch (error) {
      console.error(
        "로컬스토리지에 툴팁 상태를 저장하는데 실패했습니다:",
        error
      );
    }
  };

  // 툴팁 위치 계산 함수
  const calculateTooltipPosition = () => {
    if (tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const tooltipWidth = 400; // maxWidth 값

      // 툴팁이 화면 오른쪽을 벗어나는지 확인
      if (tooltipRect.left + tooltipWidth > windowWidth) {
        // 오른쪽 정렬로 변경
        setTooltipPosition({ left: "auto", right: 0 });
      } else {
        // 왼쪽 정렬 유지
        setTooltipPosition({ left: 0, right: "auto" });
      }
    }
  };

  // 툴팁이 보일 때 위치 계산
  useEffect(() => {
    if (tooltipVisible) {
      // 약간의 지연을 두고 계산 (DOM 업데이트 후)
      setTimeout(calculateTooltipPosition, 0);
    }
  }, [tooltipVisible]);

  return (
    <AppBar
      sx={{
        // boxShadow: 2,
        height: "60px",
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        padding: "15px",
        // height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        bgcolor: "#FDFDFD",
        width: `calc(100% - ${NAV.WIDTH + 1}px)`,
      }}
    >
      <Stack
        display={"flex"}
        flex={1}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Stack flex={1}>
          <Breadcrumb navData={[...topNavData, ...bottomNavData]} />
        </Stack>
        <Stack
          display={"flex"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              position: "relative",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={setting.guide}
                  onChange={handleGuideToggle}
                  size="small"
                />
              }
              label="이용 가이드"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: "500",
                  color: theme.palette.common.darkGrey,
                },
              }}
            />
            {tooltipVisible && (
              <Box
                ref={tooltipRef}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 12px",
                  backgroundColor: "#E3F2FD",
                  borderRadius: "8px",
                  color: "#1976d2",
                  fontSize: "14px",
                  fontWeight: "500",
                  maxWidth: "400px",
                  position: "absolute",
                  top: "100%",
                  left: tooltipPosition.left,
                  right: tooltipPosition.right,
                  marginTop: "8px",
                  zIndex: 1000,
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  sx={{
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  *
                </Box>
                <Box>관리자 이용 가이드를 통해 예약을 직접 관리해보세요.</Box>
                <Button
                  onClick={handleTooltipClose}
                  size="small"
                  sx={{
                    minWidth: "auto",
                    padding: "4px",
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.1)",
                    },
                  }}
                >
                  <Close fontSize="small" />
                </Button>
              </Box>
            )}
          </Stack>
          <Stack
            sx={{
              color: theme.palette.common.darkGrey,
              fontSize: "16px",
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {auth.user.club_admin_id}
          </Stack>
          <Stack display={"flex"} flexDirection={"row"} alignItems={"center"}>
            <Button
              onClick={handleOpen}
              size="small"
              sx={{
                mt: "2px",
                minWidth: "0px !important",
                width: "30px !important",
              }}
            >
              <DensityMedium
                fontSize="20px"
                sx={{
                  color: theme.palette.common.darkGrey,
                  verticalAlign: "middle",
                }}
              />
            </Button>

            <Popover
              open={!!open}
              anchorEl={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={logoutHandle}
                sx={{
                  typography: "body2",
                  color: "error.main",
                  py: 1.5,
                  display: "flex",
                  justifyContent: "Center",
                }}
              >
                로그아웃
              </MenuItem>
            </Popover>
          </Stack>
        </Stack>
      </Stack>
      <GuideModal open={guideModalOpen} onClose={handleGuideModalClose} />
    </AppBar>
  );
}
